// authStore — US-module-local auth surface.
//
// Spec: Epic FE-US-INT-01 §6 Phase 0. The store wraps the
// @fluxora/auth context with US-flavored selector hooks AND exposes a
// non-hook accessor used by the api-client layer (which is plain
// TypeScript and cannot call React hooks).
//
// PROVIDER-AGNOSTIC (slice 2): this store reads ONLY from the shared
// `useAuth()` AuthContext, which BOTH providers feed:
//   - <DummyAuthProvider>  (dev, devMode=true)
//   - <AuthProvider>       (real OIDC, devMode=false)
// It no longer touches the dummy-only `useDummyAuth()` / dummy selector
// hooks (those throw under the real provider, which has no DummyAuthContext).
//
// Why a separate file and not just re-export from @fluxora/auth:
//   - Country code never imports Common types as the "auth surface" by
//     accident. Everything that touches auth in US goes through here.
//   - The api-client layer needs `getCurrentAuthSnapshot()` — a function
//     that can be invoked from inside a fetch interceptor. React hooks
//     can't be called there, so we bridge via a module-scope subscription
//     to the context.

import { useEffect } from 'react';

import { type AuthUser, type DummyUser, type DummyUserRole, useAuth } from '@fluxora/auth';
import type { UserRole } from '@fluxora/types';

import { env } from '../config/env';

// --- Provider-agnostic projection -------------------------------------------
//
// Consumers (CrmShell, SignQuotePage, EditQuotePage) read the `DummyUser`
// shape (`{ id, email, displayName, role }`). The shared AuthUser uses
// `{ id, email, name, roles[] }`. We map AuthUser -> the DummyUser-compatible
// shape so those call sites are unchanged under BOTH providers.
//
// NOTE (lossy, intentional): the shared context only carries the coarse
// cross-cutting `UserRole` ('admin'|'manager'|'user'|'viewer'). The dummy
// fixtures' richer 'sales'/'ops' roles are collapsed to 'user' inside the
// dummy provider's shared projection. No app code branches on 'sales'/'ops'
// (only test mocks reference them), so this is safe. The return type stays
// widened to `DummyUserRole | UserRole` to keep typed consumers happy.

type CurrentUser = Pick<DummyUser, 'id' | 'email' | 'displayName' | 'photoUrl'> & {
  role: DummyUserRole | UserRole;
};

function projectCurrentUser(user: AuthUser | null): CurrentUser | null {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email,
    displayName: user.name,
    // First coarse role, or 'viewer' as the safe least-privilege default.
    role: user.roles[0] ?? 'viewer',
    // Undefined whenever the IdP publishes no `picture` claim — the avatar
    // then renders initials rather than calling out to a third party.
    photoUrl: user.photoUrl,
  };
}

// --- React selector hooks ---------------------------------------------------

/** Access token (in memory), or null. */
export function useAuthToken(): string | null {
  return useAuth().token;
}

/** Actor user id (`sub`), or null. */
export function useActorUserId(): string | null {
  return useAuth().user?.id ?? null;
}

/**
 * Coarse role of the current user, or null.
 *
 * ⚠️ THIS RETURNS ONLY THE FIRST ROLE, AND UNDER REAL AUTH THAT IS ALMOST
 * ALWAYS `'user'`. Prefer {@link useHasAnyRole} for authorization.
 *
 * The OIDC/ROPC provider maps realm roles into a `Set<UserRole>` and Keycloak
 * enumerates `default-roles-<realm>` first, which maps to `'user'`. So a real
 * admin projects as `roles: ['user', 'admin']` and `roles[0]` is `'user'`.
 * Measured 2026-08-08 on a live session holding `tenant:…:admin`,
 * `portal:crm:admin` AND `country:us:admin` — every one of them outranked by an
 * enumeration accident. This is safe for display ("show me a label") and WRONG
 * for a gate.
 */
export function useUserRole(): DummyUserRole | UserRole | null {
  return projectCurrentUser(useAuth().user)?.role ?? null;
}

/**
 * Does the current user hold ANY of `allowed`?
 *
 * The authorization primitive: it reads the WHOLE role array, exactly as
 * `CrmShell`'s `ROUTE_ACCESS` matrix does, so a page gate and the nav entry
 * that links to it can no longer disagree. They did — `/ops/market` rendered
 * its nav link (shell, full array) and then refused the page (`roles[0]`),
 * which is how this was found.
 */
export function useHasAnyRole(allowed: readonly string[]): boolean {
  const roles = useAuth().user?.roles ?? [];
  return allowed.some((r) => (roles as readonly string[]).includes(r));
}

/** The current user in the DummyUser-compatible shape, or null. */
export function useCurrentUser(): CurrentUser | null {
  return projectCurrentUser(useAuth().user);
}

/**
 * Returns the active tenant id. Falls back to the env-configured dev tenant
 * id so headers are never undefined — backend services reject requests
 * without `x-tenant-id`.
 */
export function useTenantId(): string {
  return useAuth().user?.tenantId ?? env.tenantId;
}

export function useIsAuthenticated(): boolean {
  return useAuth().isAuthenticated;
}

export function useIsAuthHydrated(): boolean {
  return useAuth().isHydrated;
}

/**
 * Returns a fire-and-forget logout. The shared `logout` is async under both
 * providers (OIDC does a redirect, dummy resolves immediately); callers here
 * use it synchronously, so we wrap and swallow the promise.
 */
export function useLogout(): () => void {
  const { logout } = useAuth();
  return () => {
    void logout();
  };
}

// --- Non-hook snapshot (used by api-client) ---------------------------------
//
// The api-client lives in plain TS modules and cannot call hooks. We
// maintain a module-scope `currentSnapshot` that the `<AuthStoreBridge />`
// React component keeps in sync via `useEffect`.

interface AuthSnapshot {
  token: string | null;
  tenantId: string;
  actorUserId: string | null;
  /** Human-readable display name of the current user, for audit headers
   *  (`x-actor-name`) when the token isn't verified (dev). null when absent. */
  actorName: string | null;
  role: DummyUserRole | UserRole | null;
  countryCode: string;
}

let currentSnapshot: AuthSnapshot | null = null;

export function getCurrentAuthSnapshot(): AuthSnapshot {
  return (
    currentSnapshot ?? {
      token: null,
      tenantId: env.tenantId,
      countryCode: env.devCountryCode,
      actorUserId: null,
      actorName: null,
      role: null,
    }
  );
}

/**
 * Why the session is being ended — carried to /login so the operator is told
 * "your session expired" rather than silently finding themselves signed out.
 * Mirrors `SessionEndReason` in ./sessionExpiry (declared here to keep this
 * module dependency-free; sessionExpiry imports FROM here).
 */
export type UnauthorizedReason = 'expired' | 'rejected' | 'idle';

let unauthorizedHandler: ((reason: UnauthorizedReason) => void) | null = null;

/**
 * Registers the handler invoked when the session is found to be over — an
 * api-client 401, or the CRM-wide check in `sessionExpiry` catching an expired
 * token behind a 403. The route-level guard wires this to "sign out and force
 * navigation to /login".
 */
export function setOnUnauthorizedHandler(
  handler: ((reason: UnauthorizedReason) => void) | null,
): void {
  unauthorizedHandler = handler;
  // Un-latch with the handler: the two have the same lifetime. `RequireAuth`
  // clears the handler when it unmounts — which is exactly what ending a
  // session causes — and installs a fresh one when a new session mounts the
  // guarded tree again. Anything that fails in between finds no handler and is
  // a no-op, so this cannot re-open the burst it guards against.
  if (handler === null) sessionEnded = false;
}

/**
 * True once the session has been declared over and before a new one exists.
 *
 * ⚠️ A DEAD SESSION FAILS EVERY IN-FLIGHT CALL, NOT ONE. A CRM screen fans out
 * to several services at once, so the expiry is discovered by six or eight
 * queries within the same tick — and without this latch each one would run the
 * handler again: six `logout()` calls (each POSTing `/v1/auth/logout`) and six
 * navigations. The latch lifts when a token exists again (see AuthStoreBridge).
 */
let sessionEnded = false;

/**
 * Ends the CRM session. Idempotent until the operator signs in again.
 *
 * Defaults to `'rejected'` because the historical caller is an api-client 401,
 * which says the token was refused without saying it aged out.
 */
export function notifyUnauthorized(reason: UnauthorizedReason = 'rejected'): void {
  if (sessionEnded) return;
  sessionEnded = true;
  unauthorizedHandler?.(reason);
}

// --- On-demand session refresh (403 CLAIM_REQUIRED) ---------------------------
//
// A claim granted after the access token was minted is invisible until the next
// renewal (up to the 300 s token lifetime). When a service answers 403
// `CLAIM_REQUIRED`, `claimRetry.ts` asks for ONE renewal here and retries the
// call once. This is never a sign-out: a renewal that fails leaves the session
// alone (the ROPC provider only ends it if the refresh token itself is refused,
// exactly as its timer would).

let sessionRefresher: (() => Promise<boolean>) | null = null;
let refreshInFlight: Promise<boolean> | null = null;
const tokenListeners = new Set<(token: string | null) => void>();

/** Default wait for the renewed token to reach the api-client snapshot. */
export const REFRESH_TOKEN_WAIT_MS = 3_000;

/**
 * Resolves `true` once the snapshot holds a token different from `previous`,
 * or `false` after `timeoutMs`. The provider commits the new token to React
 * state; the bridge copies it to the snapshot in an effect a tick later, and a
 * retry sent before that would carry the OLD token and be refused again.
 */
export function waitForSnapshotToken(previous: string | null, timeoutMs: number): Promise<boolean> {
  const now = currentSnapshot?.token ?? null;
  if (now !== null && now !== previous) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    const listener = (token: string | null): void => {
      if (token === null || token === previous) return;
      clearTimeout(timer);
      tokenListeners.delete(listener);
      resolve(true);
    };
    const timer = setTimeout(() => {
      tokenListeners.delete(listener);
      resolve(false);
    }, timeoutMs);
    tokenListeners.add(listener);
  });
}

/**
 * Renew the access token now and wait until api-clients send the new one.
 * Single-flight: a page that fans out to six denied calls renews ONCE.
 * `false` when the provider cannot refresh (dummy) or the renewal failed.
 */
export function requestSessionRefresh(timeoutMs: number = REFRESH_TOKEN_WAIT_MS): Promise<boolean> {
  if (refreshInFlight !== null) return refreshInFlight;
  const refresher = sessionRefresher;
  if (refresher === null) return Promise.resolve(false);
  const before = currentSnapshot?.token ?? null;
  const attempt = (async (): Promise<boolean> => {
    const renewed = await refresher().catch(() => false);
    if (!renewed) return false;
    return waitForSnapshotToken(before, timeoutMs);
  })().finally(() => {
    refreshInFlight = null;
  });
  refreshInFlight = attempt;
  return attempt;
}

/**
 * The provider's on-demand renewal, IF it offers one.
 *
 * ⚠️ US-ONLY BY DESIGN. The shared `@fluxora/auth` context exposes no renewal
 * today, and CLM-07 may not change shared packages (every other portal uses
 * them). So the CRM looks for an optional `refreshSession(): Promise<boolean>`
 * on the context object at runtime and uses it only when present. Without it
 * `requestSessionRefresh` answers `false`, a claim denial is surfaced without a
 * retry, and the provider's own timer picks up new claims within the token
 * lifetime (300 s). Nothing here changes how any other app handles a 403.
 */
export function sessionRefresherOf(ctx: object): (() => Promise<boolean>) | null {
  const fn: unknown = Reflect.get(ctx, 'refreshSession');
  if (typeof fn !== 'function') return null;
  return async (): Promise<boolean> => {
    const result: unknown = await Reflect.apply(fn, ctx, []);
    return result === true;
  };
}

/**
 * Mount this component inside the active auth provider (dummy OR real) to keep
 * the non-hook snapshot in sync with the shared AuthContext. Renders nothing.
 */
export function AuthStoreBridge(): null {
  const auth = useAuth();
  const { user, token } = auth;
  // Keyed on the provider's function itself so the bridge re-registers only
  // when the provider hands out a new one, not on every render.
  const rawRefresh: unknown = Reflect.get(auth, 'refreshSession');
  useEffect(() => {
    sessionRefresher = sessionRefresherOf({ refreshSession: rawRefresh });
    return () => {
      sessionRefresher = null;
    };
  }, [rawRefresh]);
  useEffect(() => {
    // A token in hand means a live session again — arm the session-ended latch
    // for the next expiry. Without this, one expiry would leave the CRM unable
    // to sign anyone out for the rest of the tab's life.
    if (token !== null && token.length > 0) sessionEnded = false;
    const cu = projectCurrentUser(user);
    currentSnapshot = {
      token,
      // Normal users carry their own tenant from the token; a super-admin has
      // none, so fall back to the CRM deployment's tenant (env.tenantId), which
      // the backend honors only for super-admins.
      tenantId: user?.tenantId ?? env.tenantId,
      countryCode: user?.countryCode ?? env.devCountryCode,
      actorUserId: user?.id ?? null,
      actorName: cu?.displayName ?? null,
      role: cu?.role ?? null,
    };

    // Mirror the access token into a short-lived, path-scoped cookie so the CRM
    // nginx can gate the static /docs/* portal on a live session. The API bearer
    // lives in memory / sessionStorage and can't ride the iframe's navigation
    // request; nginx `auth_request`s the Accounts service's GET /v1/auth/validate
    // (→ Keycloak userinfo) with this cookie. Path=/docs so it's sent ONLY on
    // docs requests, Secure over https, SameSite=Lax so the same-site iframe
    // subresource still carries it. Cleared on logout.
    if (typeof document !== 'undefined') {
      const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
      const attrs = `Path=/docs; SameSite=Lax${secure ? '; Secure' : ''}`;
      document.cookie =
        token !== null && token.length > 0
          ? `rh_docs_at=${encodeURIComponent(token)}; Max-Age=3600; ${attrs}`
          : `rh_docs_at=; Max-Age=0; ${attrs}`;
    }
    for (const listener of [...tokenListeners]) listener(token);
  }, [user, token]);
  return null;
}
